import React, { useState } from "react";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";
import { Checkbox } from "../../../components/ui/Checkbox";
import Button from "../../../components/ui/Button";
import Image from "../../../components/AppImage";
import ImageSourceDropdown from "./ImageSourceDropdown";
import { processImagesWithOverlay } from "../../../utils/imageOverlay";

const ObstaclesSignoffForm = ({
  surveyData,
  errors,
  onChange,
  obstaclePhotos,
  onObstaclePhotosChange,
  overlayMeta,
  capturedLocation,
  tentativePanelLocationPhoto,
  onTentativePanelLocationPhotoChange,
  tentativeInverterLocationPhoto,
  onTentativeInverterLocationPhotoChange,
  surveyorSignaturePhoto,
  onSurveyorSignaturePhotoChange,
  customerSignaturePhoto,
  onCustomerSignaturePhotoChange,
}) => {
  const handleChange = (field, value) => {
    onChange(field, value);
  };

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [processing, setProcessing] = useState({
    obstacle: false,
    panel: false,
    inverter: false,
    surveyorSig: false,
    customerSig: false,
  });

  const [signatureModal, setSignatureModal] = useState({
    open: false,
    target: null, // 'surveyor' | 'customer'
  });

  const setProcessingKey = (key, value) =>
    setProcessing((prev) => ({ ...prev, [key]: value }));

  const handleObstacleFileSelect = async (files) => {
    if (!files?.length) return;
    setProcessingKey("obstacle", true);
    try {
      const processedFiles = await processImagesWithOverlay(files, overlayMeta, capturedLocation);
      const currentPhotos = obstaclePhotos || [];
      const newPhotos = [...currentPhotos, ...processedFiles].slice(0, 5);
      onObstaclePhotosChange(newPhotos);
    } finally {
      setProcessingKey("obstacle", false);
    }
  };

  const removeObstaclePhoto = (index) => {
    const currentPhotos = obstaclePhotos || [];
    const newPhotos = currentPhotos.filter((_, i) => i !== index);
    onObstaclePhotosChange(newPhotos);
  };

  const handleSinglePhotoSelect = async (file, key, onChangeCb) => {
    if (!file) return;
    setProcessingKey(key, true);
    try {
      const [processed] = await processImagesWithOverlay([file], overlayMeta, capturedLocation);
      onChangeCb(processed);
    } finally {
      setProcessingKey(key, false);
    }
  };

  const removeSinglePhoto = (onChangeCb) => onChangeCb(null);

  const fileOrUrlToUrl = (val) => {
    if (!val) return null;
    return val instanceof File ? URL.createObjectURL(val) : val;
  };

  const canvasToFile = async (canvas, fileName) => {
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    return new File([blob], fileName, { type: "image/png" });
  };

  const SignatureModal = () => {
    if (!signatureModal.open) return null;

    let canvasRef = null;
    let ctx = null;
    let drawing = false;

    const initCanvas = (canvas) => {
      if (!canvas) return;
      canvasRef = canvas;
      ctx = canvas.getContext("2d");
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111827";
      // Fill background white
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const getPos = (e) => {
      const rect = canvasRef.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const start = (e) => {
      e.preventDefault();
      if (!ctx) return;
      drawing = true;
      const { x, y } = getPos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const move = (e) => {
      e.preventDefault();
      if (!drawing || !ctx) return;
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const end = (e) => {
      e.preventDefault();
      drawing = false;
    };

    const clear = () => {
      if (!ctx || !canvasRef) return;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasRef.width, canvasRef.height);
    };

    const save = async () => {
      const target = signatureModal.target;
      if (!target || !canvasRef) return;

      if (target === "surveyor") setProcessingKey("surveyorSig", true);
      if (target === "customer") setProcessingKey("customerSig", true);

      try {
        const file = await canvasToFile(
          canvasRef,
          `${target}-signature-${Date.now()}.png`,
        );
        if (target === "surveyor") {
          onSurveyorSignaturePhotoChange(file);
          onChange("surveyorSigned", true);
        } else {
          onCustomerSignaturePhotoChange(file);
          onChange("customerSigned", true);
        }
        setSignatureModal({ open: false, target: null });
      } finally {
        if (target === "surveyor") setProcessingKey("surveyorSig", false);
        if (target === "customer") setProcessingKey("customerSig", false);
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black/80 z-[2500] flex items-center justify-center p-4"
        onClick={() => setSignatureModal({ open: false, target: null })}
      >
        <div
          className="w-full max-w-2xl bg-card border border-border rounded-xl p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold text-foreground">
              {signatureModal.target === "surveyor"
                ? "Surveyor Signature"
                : "Customer Signature"}
            </h4>
            <Button
              variant="secondary"
              size="sm"
              iconName="X"
              onClick={() => setSignatureModal({ open: false, target: null })}
            >
              Close
            </Button>
          </div>

          <div className="rounded-lg overflow-hidden border border-border bg-white">
            <canvas
              ref={initCanvas}
              width={900}
              height={280}
              className="w-full h-48 md:h-56 touch-none"
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
            />
          </div>

          <div className="flex items-center justify-between mt-3 gap-2">
            <Button variant="outline" size="sm" onClick={clear}>
              Clear
            </Button>
            <Button variant="success" size="sm" onClick={save}>
              Save Signature
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
          <Icon name="AlertTriangle" size={20} className="text-secondary" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Obstacles & Sign-offs
        </h2>
      </div>

      <div className="space-y-6">
        {/* Obstacles */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} />
            Obstacles
          </h3>
          <div className="space-y-3">
            <Checkbox
              checked={surveyData.waterTankPresent}
              onChange={(e) =>
                handleChange("waterTankPresent", e.target.checked)
              }
              label="Water Tank Present"
            />

            <Checkbox
              checked={surveyData.neighborsWallPresent}
              onChange={(e) =>
                handleChange("neighborsWallPresent", e.target.checked)
              }
              label="Neighbor's Wall Present"
            />

            <Checkbox
              checked={surveyData.treesPresent}
              onChange={(e) => handleChange("treesPresent", e.target.checked)}
              label="Trees Present"
            />

            <Checkbox
              checked={surveyData.noObstacle}
              onChange={(e) => {
                const checked = e.target.checked;
                handleChange("noObstacle", checked);
                if (checked) {
                  handleChange("waterTankPresent", false);
                  handleChange("neighborsWallPresent", false);
                  handleChange("treesPresent", false);
                  // If "No Obstacle" is selected, obstacle photos should not be required.
                  // Clear any already-added photos and hide the section.
                  onObstaclePhotosChange?.([]);
                  setSelectedPhoto(null);
                }
              }}
              label="No Obstacle"
            />

            {!surveyData.noObstacle && (
              <>
                {/* Obstacle Photos */}
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="Camera" size={16} className="text-primary" />
                      <span className="text-sm font-semibold text-foreground">
                        Obstacle Photos
                      </span>
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {obstaclePhotos?.length || 0}/5
                    </span>
                  </div>

                  {(obstaclePhotos?.length || 0) < 5 && (
                    <ImageSourceDropdown
                      idPrefix="obstacle-photos-upload"
                      triggerLabel="Add Obstacle Photos"
                      triggerSubLabel="Photos of water tanks, walls, trees, etc."
                      multiple
                      processing={processing.obstacle}
                      disabled={(obstaclePhotos?.length || 0) >= 5}
                      onFileSelect={handleObstacleFileSelect}
                    />
                  )}

                  {obstaclePhotos?.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {obstaclePhotos.map((photo, index) => {
                        const photoUrl =
                          photo instanceof File
                            ? URL.createObjectURL(photo)
                            : photo;

                        return (
                          <div
                            key={index}
                            className="relative group rounded-lg overflow-hidden border border-border bg-muted"
                          >
                            <div className="aspect-[4/3] overflow-hidden">
                              <Image
                                src={photoUrl}
                                alt={`Obstacle photo ${index + 1}`}
                                className="w-full h-full object-contain bg-muted"
                              />
                            </div>

                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                iconName="Eye"
                                onClick={() => setSelectedPhoto(photo)}
                              >
                                View
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                iconName="Trash2"
                                onClick={() => removeObstaclePhoto(index)}
                              >
                                Remove
                              </Button>
                            </div>

                            <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              {index + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {errors?.obstaclePhotos && (
                    <p className="text-sm text-error mt-1">
                      {errors.obstaclePhotos}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tentative Locations - Photos */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="MapPin" size={16} />
            Tentative Locations (Photos)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tentative Panel Location Photo */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Image" size={16} className="text-primary" />
                  Tentative Panel Location Photo{" "}
                  <span className="text-error">*</span>
                </span>
              </div>

              {!tentativePanelLocationPhoto ? (
                <ImageSourceDropdown
                  idPrefix="tentative-panel-photo"
                  triggerLabel="Add Panel Location Photo"
                  triggerSubLabel="Upload or capture"
                  processing={processing.panel}
                  onFileSelect={(file) =>
                    handleSinglePhotoSelect(
                      file,
                      "panel",
                      onTentativePanelLocationPhotoChange,
                    )
                  }
                />
              ) : (
                <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                      src={fileOrUrlToUrl(tentativePanelLocationPhoto)}
                      alt="Tentative panel location"
                      className="w-full h-full object-contain bg-muted"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconName="Eye"
                      onClick={() =>
                        setSelectedPhoto(tentativePanelLocationPhoto)
                      }
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      onClick={() =>
                        removeSinglePhoto(onTentativePanelLocationPhotoChange)
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
              {errors?.tentativePanelLocationPhoto && (
                <p className="text-sm text-error">
                  {errors.tentativePanelLocationPhoto}
                </p>
              )}
            </div>

            {/* Tentative Inverter Location Photo */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Image" size={16} className="text-primary" />
                  Tentative Inverter Location Photo{" "}
                  <span className="text-error">*</span>
                </span>
              </div>

              {!tentativeInverterLocationPhoto ? (
                <ImageSourceDropdown
                  idPrefix="tentative-inverter-photo"
                  triggerLabel="Add Inverter Location Photo"
                  triggerSubLabel="Upload or capture"
                  processing={processing.inverter}
                  onFileSelect={(file) =>
                    handleSinglePhotoSelect(
                      file,
                      "inverter",
                      onTentativeInverterLocationPhotoChange,
                    )
                  }
                />
              ) : (
                <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                      src={fileOrUrlToUrl(tentativeInverterLocationPhoto)}
                      alt="Tentative inverter location"
                      className="w-full h-full object-contain bg-muted"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconName="Eye"
                      onClick={() =>
                        setSelectedPhoto(tentativeInverterLocationPhoto)
                      }
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      onClick={() =>
                        removeSinglePhoto(
                          onTentativeInverterLocationPhotoChange,
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
              {errors?.tentativeInverterLocationPhoto && (
                <p className="text-sm text-error">
                  {errors.tentativeInverterLocationPhoto}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sign-offs */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="CheckCircle2" size={16} />
            Sign-offs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Surveyor Signature Box */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                Surveyor Signature
              </p>
              <div
                className={`h-24 rounded-lg border-2 flex items-center justify-center text-xs md:text-sm cursor-pointer transition-colors ${
                  surveyorSignaturePhoto
                    ? "border-success bg-success/5 text-success"
                    : "border-dashed border-border bg-muted/40 text-muted-foreground hover:border-primary/70 hover:bg-primary/5"
                }`}
                onClick={() =>
                  setSignatureModal({ open: true, target: "surveyor" })
                }
              >
                {processing.surveyorSig ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                ) : surveyorSignaturePhoto ? (
                  "Signed"
                ) : (
                  "Tap here to sign as Surveyor"
                )}
              </div>
              {errors?.surveyorSignaturePhoto && (
                <p className="text-sm text-error">
                  {errors.surveyorSignaturePhoto}
                </p>
              )}
            </div>

            {/* Customer Signature Box */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                Customer Signature
              </p>
              <div
                className={`h-24 rounded-lg border-2 flex items-center justify-center text-xs md:text-sm cursor-pointer transition-colors ${
                  customerSignaturePhoto
                    ? "border-success bg-success/5 text-success"
                    : "border-dashed border-border bg-muted/40 text-muted-foreground hover:border-primary/70 hover:bg-primary/5"
                }`}
                onClick={() =>
                  setSignatureModal({ open: true, target: "customer" })
                }
              >
                {processing.customerSig ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                ) : customerSignaturePhoto ? (
                  "Signed"
                ) : (
                  "Tap here to sign as Customer"
                )}
              </div>
              {errors?.customerSignaturePhoto && (
                <p className="text-sm text-error">
                  {errors.customerSignaturePhoto}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Survey Notes */}
        <div>
          <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="FileText" size={16} />
            Additional Notes
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Survey Notes
              </label>
              <textarea
                placeholder="Enter any additional notes or observations..."
                value={surveyData.surveyNotes}
                onChange={(e) => handleChange("surveyNotes", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              {errors.surveyNotes && (
                <p className="text-sm text-error mt-1">{errors.surveyNotes}</p>
              )}
            </div>

            {/* Consent Checkbox */}
            <Checkbox
              checked={surveyData.consentAccepted}
              onChange={(e) =>
                handleChange("consentAccepted", e.target.checked)
              }
              label={`I certify that the site is feasible for a ${
                surveyData.proposedCapacityKw || "___"
              } kW installation and the customer has agreed to the tentative location of panels and inverter.`}
            />
            {errors?.consentAccepted && (
              <p className="text-sm text-error">{errors.consentAccepted}</p>
            )}
          </div>
        </div>

        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="relative max-w-4xl w-full">
              <Button
                variant="secondary"
                size="icon"
                iconName="X"
                onClick={() => setSelectedPhoto(null)}
                className="absolute -top-12 right-0"
              />
              <Image
                src={
                  selectedPhoto instanceof File
                    ? URL.createObjectURL(selectedPhoto)
                    : selectedPhoto
                }
                alt="Full size obstacle photo"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        )}

        <SignatureModal />
      </div>
    </div>
  );
};

export default ObstaclesSignoffForm;
